import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Dialog,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { useCurrentProfile } from "../../ContextWrapper";
import ClientDashboardLayout from "./ClientDashboardLayout";
import PerformanceOverviewReport from "./PerformanceOverviewReport";
import TeamExpensesReport from "./TeamExpensesReport";
import OperationsExpensesReport from "./OperationsExpensesReport";
import MarketingExpensesReport from "./MarketingExpensesReport";
import ProductsExpensesReport from "./ProductsExpensesReport";

/* ─── helpers ─── */
function toYMD(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDisplay(start, end) {
  if (!start && !end) return "";
  const fmt = (d) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  if (start && end) return `${fmt(start)} – ${fmt(end)}`;
  if (start) return `From ${fmt(start)}`;
  return `Until ${fmt(end)}`;
}

const PRIMARY = "#536DEC";
// Month names used by the custom month-range grid
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Returns true if {year, month} is between start and end (inclusive)
function monthInRange(year, month, start, end) {
  if (!start || !end) return false;
  const ts = year * 12 + month;
  const ss = start.getFullYear() * 12 + start.getMonth();
  const es = end.getFullYear() * 12 + end.getMonth();
  return ts >= ss && ts <= es;
}
function monthIsEdge(year, month, date) {
  return date && date.getFullYear() === year && date.getMonth() === month;
}

const CALENDAR_SX = (font) => ({
  "& .react-datepicker": { border: "none", fontFamily: font, width: "100%" },
  "& .react-datepicker__header": { bgcolor: "#fff", borderBottom: "1px solid #F3F4F6", pt: 1 },
  "& .react-datepicker__current-month, & .react-datepicker__day-name": { fontFamily: font },
  "& .react-datepicker__day--selected, & .react-datepicker__day--range-start, & .react-datepicker__day--range-end":
    { bgcolor: `${PRIMARY} !important`, borderRadius: "50% !important", color: "#fff !important" },
  "& .react-datepicker__day--in-range, & .react-datepicker__day--in-selecting-range":
    { bgcolor: "rgba(83,109,236,0.12) !important", color: "rgba(0,0,0,0.87) !important", borderRadius: "0 !important" },
  "& .react-datepicker__day--disabled": {
    color: "rgba(0,0,0,0.25) !important",
    backgroundColor: "transparent !important",
    opacity: 1,
    cursor: "not-allowed",
  },
  "& .react-datepicker__day--disabled:hover": {
    backgroundColor: "transparent !important",
  },
  "& .react-datepicker__day:hover": { bgcolor: "rgba(83,109,236,0.18) !important", borderRadius: "50% !important" },
  "& .react-datepicker__navigation-icon::before": { borderColor: "rgba(0,0,0,0.54)" },
});

/* ─── MonthRangeGrid ─── */
function MonthRangeGrid({ localStart, localEnd, font, onMonthClick }) {
  const today = new Date();
  // Ensure "today" includes the full day so the current date is selectable.
  today.setHours(23, 59, 59, 999);
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();
  const [displayYear, setDisplayYear] = useState(
    localStart ? localStart.getFullYear() : currentYear
  );

  const canAdvanceYear = displayYear < currentYear;

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Year navigation */}
      <Stack direction="row" alignItems="center" justifyContent="center" gap={1} sx={{ mb: 1.5 }}>
        <Box
          onClick={() => setDisplayYear((y) => y - 1)}
          sx={{ cursor: "pointer", p: 0.5, borderRadius: 1, "&:hover": { bgcolor: "#F3F4F6" }, display: "flex" }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="rgba(0,0,0,0.65)">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </Box>
        <Typography sx={{ fontFamily: font, fontSize: 14, fontWeight: 600, color: "rgba(0,0,0,0.87)", minWidth: 36, textAlign: "center" }}>
          {displayYear}
        </Typography>
        <Box
          onClick={() => { if (canAdvanceYear) setDisplayYear((y) => y + 1); }}
          sx={{
            p: 0.5,
            borderRadius: 1,
            display: "flex",
            cursor: canAdvanceYear ? "pointer" : "default",
            opacity: canAdvanceYear ? 1 : 0.25,
            "&:hover": canAdvanceYear ? { bgcolor: "#F3F4F6" } : {},
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="rgba(0,0,0,0.65)">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </Box>
      </Stack>

      {/* Month grid: 3 cols × 4 rows */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5 }}>
        {MONTH_NAMES.map((name, idx) => {
          const isFuture =
            displayYear > currentYear ||
            (displayYear === currentYear && idx > currentMonth);
          const isStart = monthIsEdge(displayYear, idx, localStart);
          const isEnd = monthIsEdge(displayYear, idx, localEnd);
          const inRange = monthInRange(displayYear, idx, localStart, localEnd);
          const inRangeNonFuture = inRange && !isFuture;
          // Never show future months as selected (even if localEnd/localStart
          // were previously set to a future value).
          const isSelected = (isStart || isEnd) && !isFuture;

          return (
            <Box
              key={name}
              onClick={() => { if (!isFuture) onMonthClick(displayYear, idx); }}
              sx={{
                textAlign: "center",
                py: 0.875,
                borderRadius: "6px",
                cursor: isFuture ? "not-allowed" : "pointer",
                border: isSelected
                  ? `1px solid ${PRIMARY}`
                  : "1px solid rgba(0,0,0,0.12)",
                bgcolor: isSelected
                  ? PRIMARY
                  : inRangeNonFuture
                  ? "rgba(83,109,236,0.08)"
                  : "transparent",
                color: isFuture
                  ? "rgba(0,0,0,0.25)"
                  : isSelected
                  ? "#fff"
                  : inRangeNonFuture
                  ? PRIMARY
                  : "rgba(0,0,0,0.75)",
                fontFamily: font,
                fontSize: 13,
                fontWeight: isSelected ? 700 : 400,
                opacity: isFuture ? 0.45 : 1,
                transition: "background 0.12s, border-color 0.12s",
                "&:hover": isFuture ? {} : {
                  bgcolor: isSelected ? PRIMARY : "rgba(83,109,236,0.14)",
                  borderColor: isSelected ? PRIMARY : "rgba(83,109,236,0.4)",
                },
              }}
            >
              {name}
            </Box>
          );
        })}
      </Box>

      {/* Selection hint */}
      <Typography sx={{ fontFamily: font, fontSize: 11, color: "rgba(0,0,0,0.38)", mt: 1.25, textAlign: "center" }}>
        {!localStart
          ? "Click a month for that month, or click two months for a range"
          : localStart.getFullYear() === localEnd.getFullYear() && localStart.getMonth() === localEnd.getMonth()
          ? `${localStart.toLocaleDateString("en-US", { month: "short", year: "numeric" })} selected. Click another month for a range, or Apply.`
          : `${localStart.toLocaleDateString("en-US", { month: "short", year: "numeric" })} – ${localEnd.toLocaleDateString("en-US", { month: "short", year: "numeric" })}`}
      </Typography>
    </Box>
  );
}

/* ─── DateRangePicker ─── */
function DateRangePicker({ startDate, endDate, onChangeStart, onChangeEnd, font }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("custom");
  const clampToToday = (d) => {
    if (!d) return null;
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const next = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(next?.getTime?.())) return null;
    return next > today ? today : next;
  };

  const [localStart, setLocalStart] = useState(
    startDate ? clampToToday(new Date(startDate)) : null
  );
  const [localEnd, setLocalEnd] = useState(
    endDate ? clampToToday(new Date(endDate)) : null
  );

  const handleApply = () => {
    const clampedStart = clampToToday(localStart);
    const clampedEnd = clampToToday(localEnd);
    setLocalStart(clampedStart);
    setLocalEnd(clampedEnd);
    onChangeStart(toYMD(clampedStart));
    onChangeEnd(toYMD(clampedEnd));
    setOpen(false);
  };

  // Month mode: one click = entire month; click same month again = stay single; click different month from single = range; click any month when range = that month only (go back to single)
  const handleMonthClick = (year, monthIdx) => {
    const today = new Date();
    if (year > today.getFullYear() || (year === today.getFullYear() && monthIdx > today.getMonth())) return;
    const firstDayClicked = new Date(year, monthIdx, 1);
    const lastDayClicked = new Date(year, monthIdx + 1, 0);

    if (!localStart) {
      // First click: single month (entire month)
      setLocalStart(firstDayClicked);
      setLocalEnd(lastDayClicked);
      return;
    }

    const isSingleMonth =
      localStart.getFullYear() === localEnd.getFullYear() && localStart.getMonth() === localEnd.getMonth();
    const clickedIsCurrentSingle =
      localStart.getFullYear() === year && localStart.getMonth() === monthIdx;

    if (isSingleMonth && clickedIsCurrentSingle) {
      // Clicking the same month again: keep single month (no change)
      return;
    }
    if (isSingleMonth && !clickedIsCurrentSingle) {
      // Single month selected, click different month: set range (min to max)
      const newStart = firstDayClicked < localStart ? firstDayClicked : localStart;
      const newEnd = lastDayClicked > localEnd ? lastDayClicked : localEnd;
      setLocalStart(newStart);
      setLocalEnd(newEnd);
      return;
    }
    // Range selected: any click = that month only (go back to single month)
    setLocalStart(firstDayClicked);
    setLocalEnd(lastDayClicked);
  };

  const handleModeChange = (m) => {
    setMode(m);
    setLocalStart(null);
    setLocalEnd(null);
  };

  const displayValue =
    startDate || endDate
      ? formatDisplay(
          startDate ? new Date(startDate) : null,
          endDate ? new Date(endDate) : null,
        )
      : null;

  return (
    <>
      {/* Trigger */}
      <Box
        onClick={() => setOpen(true)}
        sx={{
          border: open ? `1px solid ${PRIMARY}` : "1px solid rgba(0,0,0,0.23)",
          boxShadow: open ? `0 0 0 1px ${PRIMARY}` : "none",
          borderRadius: "8px",
          px: 1.5,
          py: 1,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          "&:hover": { borderColor: open ? PRIMARY : "rgba(0,0,0,0.87)" },
          bgcolor: "#fff",
          minHeight: 40,
        }}
      >
        <Typography
          sx={{
            fontFamily: font,
            fontSize: 14,
            color: displayValue ? "rgba(0,0,0,0.87)" : "rgba(0,0,0,0.38)",
            flex: 1,
            userSelect: "none",
          }}
        >
          {displayValue || "Select date range"}
        </Typography>
        <Box sx={{ color: "rgba(0,0,0,0.38)", flexShrink: 0, display: "flex" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" />
          </svg>
        </Box>
      </Box>

      {/* Modal */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            overflow: "hidden",
            width: 620,
            maxWidth: "calc(100vw - 32px)",
            m: 2,
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              background: "rgba(0, 0, 0, 0.40)",
              backdropFilter: "blur(4px)",
            },
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: "1px solid #E5E7EB" }}>
          <Typography
            sx={{
              fontFamily: font,
              fontSize: 18,
              fontWeight: 600,
              color: "#262A2C",
              lineHeight: "160%",
              letterSpacing: "0.15px",
              mb: 0.5,
            }}
          >
            Report Date
          </Typography>
          <Typography
            sx={{
              fontFamily: font,
              fontSize: 14,
              fontWeight: 400,
              color: "rgba(0,0,0,0.55)",
              lineHeight: "143%",
              letterSpacing: "0.17px",
            }}
          >
            Select date range report configuration.
          </Typography>
        </Box>

        {/* Mode toggle */}
        <Box sx={{ px: 3, pt: 2 }}>
          <Stack
            direction="row"
            sx={{
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              p: "3px",
              gap: "2px",
              bgcolor: "#F9FAFB",
            }}
          >
            {[{ key: "custom", label: "Custom dates" }, { key: "month", label: "Month range" }].map(({ key, label }) => (
              <Box
                key={key}
                onClick={() => handleModeChange(key)}
                sx={{
                  flex: 1,
                  textAlign: "center",
                  py: 0.75,
                  borderRadius: "8px",
                  cursor: "pointer",
                  bgcolor: mode === key ? "#fff" : "transparent",
                  boxShadow: mode === key ? "0 1px 3px rgba(0,0,0,0.10)" : "none",
                  transition: "all 0.15s",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 13,
                    fontWeight: mode === key ? 600 : 400,
                    color: mode === key ? PRIMARY : "rgba(0,0,0,0.55)",
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Calendar */}
        <Box sx={{ px: mode === "custom" ? 1 : 0, pt: 1 }}>
          {mode === "custom" ? (
            <Box sx={{
              px: 1, pt: 0.5, pb: 0,
              ...CALENDAR_SX(font),
              "& .react-datepicker": { display: "flex", flexDirection: "row", width: "100%" },
              "& .react-datepicker__month-container": { float: "none", flex: 1 },
            }}>
              <DatePicker
                selected={localStart}
                onChange={([s, e]) => { setLocalStart(s); setLocalEnd(e); }}
                startDate={localStart}
                endDate={localEnd}
                selectsRange
                inline
                monthsShown={2}
                maxDate={new Date(new Date().toDateString())}
                filterDate={(d) => d <= new Date(new Date().toDateString())}
              />
            </Box>
          ) : (
            <MonthRangeGrid
              localStart={localStart}
              localEnd={localEnd}
              font={font}
              onMonthClick={handleMonthClick}
            />
          )}
        </Box>

        {/* Footer */}
        <Stack
          direction="row"
          justifyContent="flex-end"
          spacing={1.5}
          sx={{ px: 3, py: 2.5, borderTop: "1px solid #F3F4F6" }}
        >
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            sx={{
              height: 36,
              px: 2,
              py: 0,
              textTransform: "none",
              borderRadius: "8px",
              borderColor: "#536DEC",
              color: "#536DEC",
              fontFamily: '"Rethink Sans"',
              fontWeight: 600,
              fontSize: "0.875rem",
              lineHeight: 1,
              ariaLabel: "Set runway goal",
              "&:hover": {
                borderColor: "#536DEC",
                bgcolor: "rgba(83, 109, 236, 0.04)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApply}
            disableElevation
            disabled={!localStart || !localEnd}
            sx={{
              fontFamily: font,
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              bgcolor: PRIMARY,
              px: 3,
              borderRadius: "8px",
              "&:hover": { bgcolor: "#4560d4" },
            }}
          >
            Apply
          </Button>
        </Stack>
      </Dialog>
    </>
  );
}

const REPORT_TYPES = [
  { value: "performance", label: "Performance report" },
  { value: "team_expenses", label: "Team expenses report" },
  { value: "operations_expenses", label: "Operations expenses report" },
  { value: "marketing_expenses", label: "Marketing expenses report" },
  { value: "products_expenses", label: "Products expenses report" },
];

const FONT = "'Rethink Sans', Helvetica, sans-serif";

export default function ReportsPage() {
  const { customer_id: routeCustomerId } = useParams();
  const { profile } = useCurrentProfile();

  const [storedCustomerId, setStoredCustomerId] = useState(() => {
    try {
      return sessionStorage.getItem("selectedCustomerId");
    } catch {
      return null;
    }
  });

  // Mirrors the same resolution used by performanceOverview so reports always
  // show the same data the user sees in the dashboard:
  //   1. URL param (internal staff / admin view)
  //   2. sessionStorage selectedCustomerId (customer already selected in session)
  //   3. First non-demo customer on the profile
  //   4. First customer (fallback)
  // Partial-setup users ("Trial Demo" / "Unpublished") are shown demo data — the
  // same substitution performanceOverview makes — so the report is never blank.
  const customerId = useMemo(() => {
    if (routeCustomerId) return routeCustomerId;

    const customers = Array.isArray(profile?.customers) ? profile.customers : [];
    const isAdmin = profile?.type === "admin";
    const demoCustomers = customers.filter((c) => c?.is_demo);
    const nonDemoCustomers = customers.filter((c) => !c?.is_demo);

    const hasTrialLike = nonDemoCustomers.some((c) =>
      ["Trial Demo", "Unpublished"].includes(c?.frontend_account_type)
    );

    // Prefer the stored selection, falling back to first non-demo then first overall.
    const trueId =
      storedCustomerId ||
      nonDemoCustomers[0]?.id?.toString() ||
      customers[0]?.id?.toString() ||
      null;

    // For partial-setup (trial/unpublished) non-admin users, substitute the demo
    // customer so they see the same demo data they see on the dashboard.
    if (!isAdmin && hasTrialLike && demoCustomers[0]?.id) {
      return String(demoCustomers[0].id);
    }

    return trueId;
  }, [routeCustomerId, storedCustomerId, profile]);

  // Keep storedCustomerId in sync when the customer switcher fires or another
  // tab changes sessionStorage (listen to both event styles for compatibility).
  useEffect(() => {
    const sync = () => {
      try {
        setStoredCustomerId(sessionStorage.getItem("selectedCustomerId"));
      } catch {
        // ignore
      }
    };
    window.addEventListener("storage", sync);
    window.addEventListener("selectedCustomerId-changed", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("selectedCustomerId-changed", sync);
    };
  }, []);

  const [reportType, setReportType] = useState("performance");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [generatedConfig, setGeneratedConfig] = useState(null);
  const [expandModalOpen, setExpandModalOpen] = useState(false);
  const expandModalHasOpenedRef = useRef(false);
  const panelRef = useRef(null);
  const printRef = useRef(null);

  if (expandModalOpen) expandModalHasOpenedRef.current = true;

  // A4 at 96 dpi — matches the hidden print container width
  const REPORT_NATIVE_WIDTH = 794;

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page { size: A4 portrait; margin: 0; }
      @media print {
        body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .no-print { display: none !important; }
      }
    `,
    documentTitle: `${{
      team_expenses: "Team Expenses Report",
      operations_expenses: "Operations Expenses Report",
      marketing_expenses: "Marketing Expenses Report",
      products_expenses: "Products Expenses Report",
    }[reportType] ?? "Performance Overview Report"} ${endDate || ""}`,
  });

  const handleGenerate = () => {
    setGeneratedConfig({ reportType, startDate, endDate: endDate || "" });
  };

  return (
    <ClientDashboardLayout
      title="Reports"
      contentMaxWidth="100%"
      contentAlign="left"
    >
      <Box sx={{ width: "100%", position: "relative" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
          gap: 3,
          alignItems: "start",
          minHeight: "calc(100vh - 180px)",
          width: "100%",
        }}
      >
        {/* ── Left: Config panel ── */}
        <Card
          sx={{
            borderRadius: 2,
            border: "1px solid #E5E7EB",
            boxShadow: "0 4px 12px rgba(15,23,42,0.06)",
            position: { md: "sticky" },
            top: { md: 24 },
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "rgba(0,0,0,0.87)", fontFamily: FONT, mb: 0.5 }}
            >
              Report configuration
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontFamily: FONT, mb: 2.5 }}
            >
              Configure your report parameters.
            </Typography>

            <Stack spacing={2.5}>
              <Box>
                <Typography
                  id="report-type-label"
                  component="span"
                  sx={{
                    fontFamily: FONT,
                    fontSize: 12,
                    color: "rgba(0,0,0,0.6)",
                    mb: 0.75,
                  }}
                >
                  Report type
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    displayEmpty
                    inputProps={{ "aria-labelledby": "report-type-label" }}
                    sx={{
                      fontFamily: FONT,
                      borderRadius: "8px",
                      color: "#6B7280",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderRadius: "8px",
                        borderColor: "#6B7280",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#6B7280" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6B7280", borderWidth: "1px" },
                      "& .MuiSelect-icon": { color: "#6B7280" },
                    }}
                    MenuProps={{
                    PaperProps: {
                      sx: {
                        borderRadius: "8px",
                        mt: 1.25,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                        "& .MuiMenuItem-root": {
                          borderRadius: "8px",
                          mx: 0.75,
                          my: 0.25,
                          "&:hover": {
                            bgcolor: "rgba(83,109,236,0.12)",
                          },
                          "&.Mui-selected": {
                          bgcolor: "rgba(83, 109, 236, 0.12)",
                            "&:hover": {
                              bgcolor: "rgba(83,109,236,0.16)",
                            },
                          },
                        },
                      },
                    },
                  }}
                >
                  {REPORT_TYPES.map((opt) => (
                    <MenuItem
                      key={opt.value}
                      value={opt.value}
                      sx={{ fontFamily: FONT }}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                  </Select>
                </FormControl>
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: 12,
                    color: "rgba(0,0,0,0.6)",
                    mb: 0.75,
                  }}
                >
                  Date range
                </Typography>
                <DateRangePicker
                  startDate={startDate}
                  endDate={endDate}
                  onChangeStart={setStartDate}
                  onChangeEnd={setEndDate}
                  font={FONT}
                />
              </Box>

              <Button
                variant="contained"
                fullWidth
                onClick={handleGenerate}
                sx={{
                  mt: 0.5,
                  py: 1.25,
                  fontFamily: FONT,
                  fontWeight: 600,
                  textTransform: "none",
                  bgcolor: "#536DEC",
                  borderRadius: "8px",
                  "&:hover": { bgcolor: "#4560d4" },
                }}
              >
                Generate Report
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* ── Right: Report preview ── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(15,23,42,0.06)",
            overflow: "hidden",
            height: "calc(100vh - 180px)",
            minHeight: 600,
          }}
        >
          {/* ── White header bar ── */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 2.5,
              py: 2,
              bgcolor: "#fff",
              borderBottom: "1px solid #E5E7EB",
              flexShrink: 0,
            }}
          >
            {/* Title + date range */}
            <Box>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontWeight: 700,
                  fontSize: 16,
                  color: "#262A2C",
                  lineHeight: "150%",
                }}
              >
                {generatedConfig
                  ? ({
                      team_expenses: "Team Expenses Overview Report",
                      operations_expenses: "Operations Expenses Overview Report",
                      marketing_expenses: "Marketing Expenses Overview Report",
                      products_expenses: "Products Expenses Overview Report",
                    }[generatedConfig.reportType] ?? "Performance Overview Report")
                  : "Report Preview"}
              </Typography>
              {generatedConfig && (generatedConfig.startDate || generatedConfig.endDate) && (
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 400,
                    fontSize: 13,
                    color: "rgba(0,0,0,0.55)",
                    mt: 0.25,
                  }}
                >
                  {[generatedConfig.startDate, generatedConfig.endDate].filter(Boolean).join(" – ")}
                </Typography>
              )}
            </Box>

            {/* PDF label + Export button — only when report is generated */}
            {generatedConfig && (
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0 }}>
                {/* PDF format indicator */}
                {/* <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.75}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    border: "1px solid #E5E7EB",
                    borderRadius: "6px",
                    bgcolor: "#fff",
                  }}
                >
                  <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 500, color: "#262A2C" }}>
                    PDF
                  </Typography>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="rgba(0,0,0,0.45)">
                    <path d="M7 10l5 5 5-5z" />
                  </svg>
                </Stack> */}

                <Button
                  onClick={handlePrint}
                  variant="contained"
                  disableElevation
                  sx={{
                    fontFamily: FONT,
                    fontWeight: 600,
                    fontSize: 14,
                    textTransform: "none",
                    color: "#fff",
                    bgcolor: "#536DEC",
                    px: 2.5,
                    py: 1,
                    borderRadius: "8px",
                    gap: 1,
                    "&:hover": { bgcolor: "#4560d4" },
                  }}
                >
                  <Box component="img" src="/download_white.svg" alt="" sx={{ width: 18, height: 18 }} />
                  Export
                </Button>
              </Stack>
            )}
          </Stack>

          {/* ── Grey preview area ── */}
          <Box
            ref={panelRef}
            sx={{
              flex: 1,
              overflow: "auto",
              bgcolor: "#F3F4F6",
              position: "relative",
              p: generatedConfig ? 3 : 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {/* Expand button — opens report in modal */}
            {generatedConfig && (
              <ButtonBase
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setExpandModalOpen(true);
                }}
                title="Expand"
                sx={{
                  position: "sticky",
                  top: 12,
                  alignSelf: "flex-end",
                  zIndex: 20,
                  width: 34,
                  height: 34,
                  minWidth: 34,
                  minHeight: 34,
                  p: 0,
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  border: "1px solid rgba(0,0,0,0.12)",
                  backgroundColor: "#fff !important",
                  background: "#fff !important",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                  "&:hover": {
                    background: "#e7e7e7 !important",
                    borderColor: "#536DEC",
                  },
                  "&:active": {
                    backgroundColor: "rgba(83,109,236,0.16) !important",
                    background: "rgba(83,109,236,0.16) !important",
                  },
                  transition: "background 0.15s, border-color 0.15s",
                }}
              >
                <Box component="img" src="/expand.svg" alt="Expand" sx={{ width: 18, height: 18, pointerEvents: "none" }} />
              </ButtonBase>
            )}

            {generatedConfig ? (
              <Box
                sx={{
                  width: REPORT_NATIVE_WIDTH,
                  boxShadow: "0 2px 16px rgba(0,0,0,0.10)",
                  borderRadius: "8px",
                  overflow: "hidden",
                  bgcolor: "#fff",
                  flexShrink: 0,
                }}
              >
                {(() => {
                  const sharedProps = {
                    ref: printRef,
                    customerId,
                    startDate: generatedConfig.startDate,
                    endDate: generatedConfig.endDate,
                  };
                  switch (generatedConfig.reportType) {
                    case "team_expenses":      return <TeamExpensesReport {...sharedProps} />;
                    case "operations_expenses": return <OperationsExpensesReport {...sharedProps} />;
                    case "marketing_expenses":  return <MarketingExpensesReport {...sharedProps} />;
                    case "products_expenses":   return <ProductsExpensesReport {...sharedProps} />;
                    default:
                      return <PerformanceOverviewReport {...sharedProps} reportType={generatedConfig.reportType} />;
                  }
                })()}
              </Box>
            ) : (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ flex: 1, width: "100%", height: "100%", py: 6, px: 3, textAlign: "center" }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, color: "rgba(0,0,0,0.87)", fontFamily: FONT, mb: 1 }}
                >
                  No Report Generated
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontFamily: FONT, maxWidth: 360 }}
                >
                  Configure your report parameters and click "Generate Report" to view results.
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>
      </Box>

        {/* Expand modal — report on backdrop, scrollable, no border */}
        <Dialog
          open={expandModalOpen}
          onClose={() => setExpandModalOpen(false)}
          maxWidth={false}
          fullWidth
          keepMounted
          hideBackdrop={false}
          PaperProps={{
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
              border: "none",
              maxWidth: "calc(100vw - 32px)",
              width: "max-content",
              minWidth: 200,
              height: "calc(100vh - 32px)",
              maxHeight: "calc(100vh - 32px)",
              m: 2,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            },
          }}
          slotProps={{
            backdrop: {
              sx: {
                background: "rgba(0, 0, 0, 0.40)",
                backdropFilter: "blur(4px)",
              },
            },
          }}
        >
          {/* Scrollable area — flex child so it gets fixed height and scrolls */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
            }}
          >
            {/* Close button — top right corner */}
            <Box
              component="button"
              type="button"
              onClick={() => setExpandModalOpen(false)}
              aria-label="Close"
              sx={{
                position: "fixed",
                top: 24,
                right: 24,
                zIndex: 1301,
                border: "none",
                background: "rgba(255,255,255,0.95)",
                cursor: "pointer",
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(0,0,0,0.7)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                "&:hover": { background: "#fff", color: "rgba(0,0,0,0.87)" },
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </Box>
            {/* Content wrapper — centers report */}
            <Box sx={{ display: "flex", justifyContent: "center", py: 2, px: 2 }}>
            {generatedConfig && (expandModalOpen || expandModalHasOpenedRef.current) && (
              <Box
                sx={{
                  width: REPORT_NATIVE_WIDTH,
                  overflow: "hidden",
                  bgcolor: "#fff",
                  borderRadius: "8px",
                }}
              >
                {(() => {
                  const sharedProps = {
                    customerId,
                    startDate: generatedConfig.startDate,
                    endDate: generatedConfig.endDate,
                  };
                  switch (generatedConfig.reportType) {
                    case "team_expenses":
                      return <TeamExpensesReport {...sharedProps} />;
                    case "operations_expenses":
                      return <OperationsExpensesReport {...sharedProps} />;
                    case "marketing_expenses":
                      return <MarketingExpensesReport {...sharedProps} />;
                    case "products_expenses":
                      return <ProductsExpensesReport {...sharedProps} />;
                    default:
                      return <PerformanceOverviewReport {...sharedProps} reportType={generatedConfig.reportType} />;
                  }
                })()}
              </Box>
            )}
            </Box>
          </Box>
        </Dialog>
      </Box>
    </ClientDashboardLayout>
  );
}
